import { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader } from 'components';
import {
  MANUAL_NOTE_SECTIONS,
  type ManualNoteSection,
  type ManualNotes,
} from 'hooks/useManualNotes';
import { useTranslation } from 'react-i18next';
import { NoteDictation } from './NoteDictation';
import { NotesGrid, StyledCardDescription, StyledCardTitle } from './RecordingNotes.styles';
import { TextBuildingBlockTextarea } from './TextBuildingBlockTextarea';

interface RecordingNotesProps {
  notes: ManualNotes;
  onSectionChange: (section: ManualNoteSection, value: string) => void;
  canDictate?: boolean;
}

export const RecordingNotes = ({
  notes,
  onSectionChange,
  canDictate = false,
}: RecordingNotesProps) => {
  const { t, i18n } = useTranslation();
  const [dictatingSection, setDictatingSection] = useState<string>();
  const handleDictatingChange = useCallback((section: string, isDictating: boolean) => {
    setDictatingSection((current) =>
      isDictating ? section : current === section ? undefined : current
    );
  }, []);

  return (
    <Card>
      <CardHeader>
        <StyledCardTitle>{t('recording.notes.title')}</StyledCardTitle>
        <StyledCardDescription>{t('recording.notes.description')}</StyledCardDescription>
      </CardHeader>
      <CardContent>
        <NotesGrid>
          {MANUAL_NOTE_SECTIONS.map((section) => {
            const helperKey = `recording.notes.sections.${section}.helper`;
            const label = t(`recording.notes.sections.${section}.label`);
            return (
              <TextBuildingBlockTextarea
                key={section}
                section={section}
                id={`recording-notes-${section}`}
                label={label}
                helper={i18n.exists(helperKey) ? t(helperKey) : undefined}
                className="min-h-16 text-xs placeholder:text-xs"
                value={notes[section]}
                placeholder={t(`recording.notes.sections.${section}.placeholder`)}
                onValueChange={(value) => onSectionChange(section, value)}
                renderActions={
                  canDictate
                    ? (editor) => (
                        <NoteDictation
                          editor={editor}
                          section={section}
                          label={label}
                          dictatingSection={dictatingSection}
                          onDictatingChange={handleDictatingChange}
                        />
                      )
                    : undefined
                }
              />
            );
          })}
        </NotesGrid>
      </CardContent>
    </Card>
  );
};
